import React from 'react';
import { AppBar, Toolbar, Button, Container, Box, IconButton, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { FaBars, FaHome, FaTh, FaEnvelope } from 'react-icons/fa';
import logoEcotij from '../logo.png';

const Header = () => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const location = useLocation();
  const appBarRef = React.useRef(null);
  const [headerBottom, setHeaderBottom] = React.useState(80);

  React.useLayoutEffect(() => {
    const update = () => {
      if (appBarRef.current) {
        const rect = appBarRef.current.getBoundingClientRect();
        setHeaderBottom(Math.max(0, Math.round(rect.bottom)));
      }
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, { passive: true });
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update);
    };
  }, [mobileOpen]);

  const handleDrawerToggle = () => setMobileOpen((v) => !v);
  const isActive = (path) => location.pathname === path;

  const menuItems = [
    { label: 'Accueil', path: '/', icon: <FaHome /> },
    { label: 'Catalogue', path: '/catalogue', icon: <FaTh /> },
    { label: 'Contact', path: '/contact', icon: <FaEnvelope /> },
  ];

  return (
    <>
      <AppBar
        ref={appBarRef}
        position="sticky"
        elevation={0}
        sx={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid #e2e8f0',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: { xs: '70px', sm: '80px' } }}>
            {/* Logo */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                transition: 'all 0.3s ease',
                '&:hover': { opacity: 0.85, transform: 'scale(1.05)' },
              }}
            >
              <Box
                component="img"
                src={logoEcotij}
                alt="ECOTIJ"
                sx={{
                  width: { xs: 200, md: 240 },
                  height: 'auto',
                  maxHeight: { xs: 65, md: 85 },
                  objectFit: 'contain',
                  display: 'block',
                  mixBlendMode: 'multiply',
                  filter: 'contrast(1.1) brightness(1.05)'
                }}
              />
            </Box>

            {/* Desktop Menu */}
            <Box sx={{ display: { xs: 'none', lg: 'flex' }, gap: 0.5, alignItems: 'center' }}>
              {menuItems.map((item) => (
                <Button
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  startIcon={item.icon}
                  sx={{
                    fontWeight: 700,
                    px: 2.5,
                    py: 1.25,
                    fontSize: '0.938rem',
                    color: isActive(item.path) ? '#0077B8' : '#64748b',
                    textTransform: 'none',
                    borderRadius: '10px',
                    bgcolor: isActive(item.path) ? 'rgba(0, 119, 184, 0.08)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(0, 119, 184, 0.1)', color: '#0077B8' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>

            {/* Mobile Menu Button */}
            <IconButton
              color="inherit"
              aria-label="menu"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{
                display: { xs: 'flex', lg: 'none' },
                color: '#0077B8',
                bgcolor: 'rgba(0, 119, 184, 0.1)',
                borderRadius: '10px',
                p: 1.5,
                '&:hover': { bgcolor: 'rgba(0, 119, 184, 0.15)' },
              }}
            >
              <FaBars size={20} />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile dropdown menu under the navbar */}
      {mobileOpen && (
        <>
          {/* Backdrop to close on outside click */}
          <Box
            onClick={handleDrawerToggle}
            sx={{
              position: 'fixed',
              top: `${headerBottom}px`,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(15,23,42,0.35)',
              zIndex: 1300,
              display: { xs: 'block', lg: 'none' },
            }}
          />
          <Box
            sx={{
              position: 'fixed',
              top: `${headerBottom}px`,
              left: 0,
              right: 0,
              zIndex: 1301,
              display: { xs: 'block', lg: 'none' },
              px: { xs: 2, sm: 3 },
              '@keyframes drop': {
                from: { opacity: 0, transform: 'translateY(-8px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <Container maxWidth="xl" sx={{ px: 0 }}>
              <Box
                sx={{
                  bgcolor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  borderBottomLeftRadius: '12px',
                  borderBottomRightRadius: '12px',
                  boxShadow: '0 10px 30px rgba(2,8,23,0.12)',
                  animation: 'drop 160ms ease-out',
                  overflow: 'hidden',
                }}
              >
                <List sx={{ p: 1 }}>
                  {menuItems.map((item) => (
                    <ListItem key={item.path} disablePadding>
                      <ListItemButton
                        component={RouterLink}
                        to={item.path}
                        onClick={handleDrawerToggle}
                        sx={{
                          borderRadius: '10px',
                          py: 1.5,
                          px: 2,
                          m: 0.5,
                          bgcolor: isActive(item.path) ? 'rgba(2,132,199,0.08)' : 'transparent',
                          border: isActive(item.path) ? '1px solid rgba(2,132,199,0.25)' : '1px solid transparent',
                          '&:hover': { bgcolor: 'rgba(2,132,199,0.08)' },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <Box sx={{ mr: 2, display: 'flex', color: isActive(item.path) ? '#0284c7' : '#334155' }}>
                          {item.icon}
                        </Box>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{ fontWeight: isActive(item.path) ? 800 : 600, fontSize: '1rem' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Container>
          </Box>
        </>
      )}
    </>
  );
};

export default Header;
